// Central, CMS-ready content source for Cloud Beaker.
// Editing this file (or a future admin API backed by it) updates the site.

export const CONTACT = {
  email: "hello@cloudbeaker.com",
  sales: "architects@cloudbeaker.com",
  phone: "+1 (210) 201-2513",
  address: "Cloud Beaker Corporation • Global Delivery",
};

// Google Calendar Appointment Scheduling — booking/consultation/appointment flows.
export const BOOKING_URL =
  "https://calendar.google.com/calendar/appointments/schedules/AcZssZ3JeCHcfvhP_ZOj_UI6uRPPx2p49JXQD-TX-MWb-kdSe6qN2ddJugCOqLvJ7fHscgiYufJhbLIt?gv=true";

export const IMAGES = {
  dataCenter:
    "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1600&q=80",
  collaboration:
    "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1600&q=80",
  engineers:
    "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80",
  network:
    "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1600&q=80",
  executive:
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1600&q=80",
  servers:
    "https://images.unsplash.com/photo-1580894742597-87bc8789db3d?auto=format&fit=crop&w=1600&q=80",
  ai:
    "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=80",
  office:
    "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=80",
  security:
    "https://images.unsplash.com/photo-1510915228340-29c85a43dcfe?auto=format&fit=crop&w=1600&q=80",
};

// Hero slideshow slides (three approved hero concepts). Photos are the
// approved artworks with text/buttons/partner-strip separated out (rendered live).
const HB = (process.env.PUBLIC_URL || "") + "/brand";
export const HERO_SLIDES = [
  {
    id: "results",
    eyebrow: "Multi-Cloud Transformation Partner",
    line1: "One Partner.",
    line2: "Every Cloud.",
    line3: "Real Results.",
    main: "blue",
    accent: "green",
    paragraph:
      "Strategic cloud solutions across Google Cloud, Microsoft and Alibaba Cloud to help you innovate, operate and grow without limits.",
    cta1: { label: "Schedule a Consultation", to: "/book" },
    cta2: { label: "Watch Video", to: "/resources" },
    image: `${HB}/hero-slide-1.jpg`,
  },
  {
    id: "higher",
    eyebrow: "Multi-Cloud Transformation Partner",
    line1: "Higher",
    line2: "Possibilities",
    line3: "in the Cloud.",
    main: "blue",
    accent: "green",
    paragraph:
      "Strategy. Implementation. Management. Across Google Cloud, Microsoft and Alibaba Cloud.",
    cta1: { label: "Get Started", to: "/book" },
    cta2: { label: "Talk to an Expert", to: "/book" },
    image: `${HB}/hero-slide-2.jpg`,
  },
  {
    id: "build",
    eyebrow: "Multi-Cloud Transformation Partner",
    line1: "Build Smarter.",
    line2: "Scale Faster.",
    line3: "Go Further.",
    main: "ink",
    accent: "green",
    paragraph:
      "Cloud Beaker helps organizations design, migrate, modernize and manage their cloud infrastructure across Google Cloud, Microsoft and Alibaba Cloud.",
    cta1: { label: "Get Started", to: "/book" },
    cta2: { label: "Talk to an Expert", to: "/book" },
    image: `${HB}/hero-slide-3.jpg`,
  },
];

export const SOLUTIONS = [
  {
    slug: "cloud-strategy",
    title: "Cloud Strategy",
    icon: "Cloud",
    tagline: "Plan, design and migrate with confidence across any cloud.",
    description:
      "Senior-led strategy that aligns your cloud roadmap to business outcomes—covering target architecture, operating model, cost and risk before a single workload moves. We assess where you are today, define where you want to be, and build a pragmatic, phased plan to get there. The result is a board-ready roadmap your teams can execute with confidence and your stakeholders can fund without hesitation.",
    bullets: [
      "Cloud readiness & maturity assessment",
      "Target-state architecture & landing zones",
      "Business case, TCO and ROI modelling",
      "Multi-cloud operating model & governance",
    ],
    outcomes: ["Clear roadmap", "Lower risk", "Faster approvals"],
  },
  {
    slug: "cloud-migration",
    title: "Cloud Migration",
    icon: "MoveRight",
    tagline: "Move workloads safely with proven migration factories.",
    description:
      "Rehost, replatform or refactor with repeatable migration factories, automated tooling and zero-drama cutovers across Google Cloud, Azure and Alibaba Cloud. Every wave is planned around dependencies, risk and business priorities, then validated with automated testing before go-live. You get predictable timelines, minimal downtime and a landing zone engineered for security and scale from day one.",
    bullets: [
      "Discovery, dependency mapping & wave planning",
      "Landing zone & network foundation",
      "Automated migration factory & testing",
      "Cutover, hypercare and optimization",
    ],
    outcomes: ["Minimal downtime", "Predictable waves", "Validated cutovers"],
  },
  {
    slug: "application-modernization",
    title: "Application Modernization",
    icon: "Boxes",
    tagline: "Modernize, rebuild and innovate for what’s next.",
    description:
      "Containerize, refactor to microservices and adopt serverless to cut cost and accelerate delivery—without betting the business on a big-bang rewrite. We modernize incrementally, prioritizing the applications that unlock the most value first and de-risking every step. The outcome is software that ships faster, scales elastically and costs less to run.",
    bullets: [
      "Containers & Kubernetes (GKE / AKS)",
      "Serverless & event-driven architecture",
      "API-first & microservices decomposition",
      "CI/CD, DevOps and platform engineering",
    ],
    outcomes: ["Faster releases", "Lower run cost", "Higher resilience"],
  },
  {
    slug: "managed-cloud",
    title: "Managed Cloud Operations",
    icon: "Settings",
    tagline: "Reliable, secure and cost-efficient operations at scale.",
    description:
      "A 24/7 extension of your team—monitoring, patching, incident response and continuous optimization backed by clear SLAs across every cloud you run. We combine automation with experienced engineers so issues are caught early and resolved fast. Your people stay focused on innovation while we keep the platform secure, available and efficient.",
    bullets: [
      "24/7 monitoring & incident response",
      "Patching, backup & DR management",
      "SLA-backed operations & reporting",
      "Continuous cost & performance tuning",
    ],
    outcomes: ["99.95% SLAs", "Fewer incidents", "Optimized spend"],
  },
  {
    slug: "cloud-security",
    title: "Security & Compliance",
    icon: "ShieldCheck",
    tagline: "Build a stronger, more resilient business in the cloud.",
    description:
      "Zero-trust architecture, posture management and compliance mapping that protects identities, data and workloads across your multi-cloud estate. We harden your environment, monitor it around the clock and align controls to the frameworks your auditors expect. Security becomes a business enabler—reducing exposure while keeping you audit-ready.",
    bullets: [
      "Zero-trust & identity (Entra ID / IAM)",
      "Cloud security posture management (CSPM)",
      "Threat detection & 24/7 SOC",
      "Compliance mapping (SOC 2, ISO, HIPAA)",
    ],
    outcomes: ["Reduced exposure", "Audit-ready", "Faster response"],
  },
  {
    slug: "ai-data",
    title: "AI / Data / Analytics",
    icon: "BrainCircuit",
    tagline: "Turn your data into intelligence with modern platforms and AI.",
    description:
      "Modern data platforms, real-time analytics and generative AI—built on BigQuery, Vertex AI, Azure AI and Copilot—to unlock measurable value from your data. We engineer trusted, governed foundations first, then layer the analytics and AI experiences people actually use. The result is faster, better decisions and AI you can scale responsibly.",
    bullets: [
      "Data platform & lakehouse engineering",
      "BigQuery / Synapse analytics",
      "Generative AI (Vertex AI, Azure OpenAI)",
      "MLOps & responsible AI governance",
    ],
    outcomes: ["Faster insight", "AI at scale", "Governed data"],
  },
  {
    slug: "disaster-recovery",
    title: "Backup & Disaster Recovery",
    icon: "HardDriveDownload",
    tagline: "Protect what matters with resilient backup and DR.",
    description:
      "Business continuity engineered to your RPO/RTO—automated backups, cross-region replication and tested recovery runbooks across clouds. We design for the failures that matter, then prove resilience with regular, documented recovery tests. When disruption strikes, you recover quickly and predictably—not hopefully.",
    bullets: [
      "Backup strategy & automation",
      "Cross-region & cross-cloud replication",
      "RPO / RTO design & DR runbooks",
      "Regular recovery testing & reporting",
    ],
    outcomes: ["Meets RPO/RTO", "Tested recovery", "Peace of mind"],
  },
  {
    slug: "finops",
    title: "Cloud FinOps",
    icon: "PiggyBank",
    tagline: "See, understand and optimize every cloud dollar.",
    description:
      "Bring engineering, finance and leadership together with real-time visibility, accountability and continuous optimization of your multi-cloud spend. We turn cloud cost from a monthly surprise into a managed, forecastable discipline. Expect meaningful savings, clear ownership and guardrails that prevent waste before it happens.",
    bullets: [
      "Cost visibility & showback/chargeback",
      "Commitment & rightsizing optimization",
      "Budget guardrails & anomaly alerts",
      "FinOps operating model & culture",
    ],
    outcomes: ["20–40% savings", "Cost accountability", "No surprises"],
  },
  {
    slug: "modern-workplace",
    title: "Modern Work & Collaboration",
    icon: "Users",
    tagline: "Empower your people with modern work solutions.",
    description:
      "Google Workspace and Microsoft 365 deployments—with security, adoption and Copilot enablement—that make hybrid teams productive and secure. We handle migration, governance and change management so your people adopt new tools with confidence, not friction. The payoff is a modern, secure workplace where collaboration simply works.",
    bullets: [
      "Google Workspace & Microsoft 365",
      "Copilot & Gemini enablement",
      "Endpoint management (Intune)",
      "Change management & adoption",
    ],
    outcomes: ["Higher adoption", "Secure endpoints", "Productive teams"],
  },
  {
    slug: "cloud-licensing",
    title: "Cloud Licensing & Procurement",
    icon: "KeyRound",
    tagline: "Right licensing, right price, fully managed.",
    description:
      "As a Microsoft CSP and cloud partner, we simplify licensing and procurement—optimizing agreements, renewals and spend across your providers. We right-size entitlements, streamline renewals and consolidate billing into a single, predictable invoice. You get the licenses you need at the right price, actively managed so nothing lapses or overspends.",
    bullets: [
      "Microsoft CSP & agreement optimization",
      "Google & Alibaba Cloud procurement",
      "License true-ups & renewals",
      "Single, consolidated billing",
    ],
    outcomes: ["Optimized licensing", "One invoice", "Managed renewals"],
  },
];

// Six homepage cards (order per approved reference)
export const HOME_SOLUTIONS = [
  { slug: "cloud-strategy", title: "Cloud Strategy & Migration", icon: "Cloud", blurb: "Plan, design and migrate with confidence across any cloud." },
  { slug: "ai-data", title: "AI / Data / Analytics", icon: "BarChart3", blurb: "Turn your data into intelligence with modern data platforms and AI." },
  { slug: "application-modernization", title: "Application Modernization", icon: "Boxes", blurb: "Modernize, rebuild and innovate for what’s next." },
  { slug: "managed-cloud", title: "Managed Cloud Operations", icon: "Settings", blurb: "Reliable, secure and cost-efficient cloud operations at scale." },
  { slug: "cloud-security", title: "Security & Compliance", icon: "ShieldCheck", blurb: "Build a stronger, more resilient business in the cloud." },
  { slug: "modern-workplace", title: "Modern Work & Collaboration", icon: "Users", blurb: "Empower your people with modern work solutions." },
];

export const PLATFORMS = [
  {
    slug: "google-cloud", title: "Google Cloud", mark: "GoogleCloudMark", relationship: "Google Cloud Partner",
    blurb: "Build what’s next with Google Cloud—from GKE and BigQuery to Vertex AI and Gemini. As a Google Cloud Partner, we help you migrate, modernize and innovate on a platform engineered for data and AI at scale.",
    capabilities: ["Cloud assessment & migration", "Compute, networking & storage", "GKE & Cloud Run", "BigQuery data platform", "Vertex AI & Gemini", "Database modernization", "Security & FinOps", "Google Workspace"],
  },
  {
    slug: "google-workspace", title: "Google Workspace", mark: "GoogleCloudMark", relationship: "Google Cloud Partner",
    blurb: "Secure, collaborate and work smarter with Google Workspace and Gemini. We deliver seamless migrations, strong data protection and adoption programs that help teams get more done, from anywhere.",
    capabilities: ["Deployment & migration", "Security & DLP", "Gemini for Workspace", "Change management & adoption", "Admin & governance"],
  },
  {
    slug: "microsoft-azure", title: "Microsoft Azure", mark: "MicrosoftMark", relationship: "Microsoft Cloud Solution Provider",
    blurb: "Innovate with Azure—migration, AI, security and modern infrastructure at enterprise scale. As a Microsoft Cloud Solution Provider, we bring landing zones, governance and Copilot-ready AI to your estate.",
    capabilities: ["Azure migration & landing zones", "Azure networking", "Azure AI & Copilot", "Azure Virtual Desktop & Windows 365", "Azure Backup & Site Recovery", "Defender & Entra ID", "Licensing & CSP"],
  },
  {
    slug: "microsoft-365", title: "Microsoft 365", mark: "MicrosoftMark", relationship: "Microsoft Cloud Solution Provider",
    blurb: "Modern work with Microsoft 365, Copilot, Intune and Defender. We deploy, secure and optimize your M365 environment so people stay productive and protected everywhere they work.",
    capabilities: ["M365 deployment & migration", "Copilot enablement", "Intune endpoint management", "Defender security", "Entra ID identity", "Licensing optimization"],
  },
  {
    slug: "alibaba-cloud", title: "Alibaba Cloud", mark: "AlibabaCloudMark", relationship: "Alibaba Cloud Partner",
    blurb: "Scale globally with Alibaba Cloud—built for APAC deployments and international expansion. We architect secure, compliant foundations that help you enter new markets with confidence.",
    capabilities: ["Compute, networking & storage", "Databases & CDN", "Security & AI", "Migration & backup", "APAC deployments", "International expansion"],
  },
  {
    slug: "hybrid-cloud", title: "Hybrid Cloud & Edge", mark: "HybridEdgeMark", relationship: "Multi-cloud architecture",
    blurb: "Connect everything, anywhere—hybrid, edge and on-premises integration done right. We unify operations across clouds and data centers so you get consistency, control and reach without complexity.",
    capabilities: ["Hybrid connectivity & networking", "Edge computing", "On-premises integration", "Multi-cloud architecture", "Unified operations"],
  },
];

export const INDUSTRIES = [
  { slug: "healthcare", title: "Healthcare", icon: "HeartPulse", blurb: "HIPAA-ready cloud for care delivery, data and AI—engineered to protect patient information while accelerating digital services.", points: ["Compliance & data protection", "Interoperability & analytics", "Secure collaboration"] },
  { slug: "financial-services", title: "Financial Services", icon: "Landmark", blurb: "Resilient, compliant cloud for modern finance—built for regulatory scrutiny, real-time risk and always-on availability.", points: ["Regulatory compliance", "Real-time risk & analytics", "Zero-trust security"] },
  { slug: "retail", title: "Retail", icon: "ShoppingBag", blurb: "Scalable commerce, data and personalization—ready for peak demand and the unified customer experiences shoppers expect.", points: ["Elastic scale for peak", "Unified customer data", "AI personalization"] },
  { slug: "professional-services", title: "Professional Services", icon: "Briefcase", blurb: "Modern work and delivery for people-led firms—secure collaboration and analytics that help teams win and deliver more.", points: ["Modern workplace", "Secure collaboration", "Project analytics"] },
  { slug: "manufacturing", title: "Manufacturing", icon: "Factory", blurb: "Connected operations from edge to cloud—turning shop-floor data into efficiency, quality and predictive insight.", points: ["IoT & edge", "Supply-chain analytics", "Predictive maintenance"] },
  { slug: "public-sector", title: "Public Sector", icon: "Building2", blurb: "Secure, compliant cloud for public services—balancing data sovereignty, citizen experience and cost transparency.", points: ["Sovereignty & compliance", "Citizen services", "Cost transparency"] },
  { slug: "technology", title: "Technology", icon: "Cpu", blurb: "Cloud-native scale for software and platforms—engineering, global reach and AI enablement to help you grow fast.", points: ["Cloud-native engineering", "Global scale", "AI product enablement"] },
];

export const RESOURCES = [
  { slug: "insights", title: "Insights", icon: "Sparkles", blurb: "Perspectives on multi-cloud strategy, AI and modernization from our architects." },
  { slug: "case-studies", title: "Case Studies", icon: "BadgeCheck", blurb: "Outcomes from real multi-cloud transformations across industries." },
  { slug: "whitepapers", title: "Whitepapers", icon: "FileText", blurb: "In-depth guidance and reference architectures for cloud leaders." },
  { slug: "cloud-guides", title: "Cloud Guides", icon: "Layers", blurb: "Practical playbooks for migration, security and day-two operations." },
  { slug: "news", title: "News", icon: "LineChart", blurb: "Announcements, partnerships and updates from Cloud Beaker." },
];

// Reusable, non-fabricated case study templates (industries anonymized).
export const CASE_STUDIES = [
  {
    slug: "global-retailer-gcp", industry: "Retail", platform: "Google Cloud",
    title: "Global retailer scales peak-season commerce on Google Cloud",
    challenge: "Seasonal traffic spikes overwhelmed legacy infrastructure, causing outages during peak sales periods.",
    architecture: "GKE-based microservices, Cloud Load Balancing, BigQuery analytics and Cloud CDN.",
    migrationScope: "120+ services replatformed across three regions.",
    technology: ["GKE", "BigQuery", "Cloud CDN", "Cloud SQL"],
    services: ["Cloud Strategy", "Migration", "Managed Operations"],
    timeline: "6 months",
    outcome: "Zero downtime through peak season with elastic scale and faster page loads.",
    metrics: [{ label: "Peak uptime", value: "100%" }, { label: "Infra cost", value: "-28%" }, { label: "Deploy speed", value: "4x" }],
    quote: "Cloud Beaker gave us the confidence to scale without fear during our biggest revenue days.",
    quoteRole: "VP of Engineering, Global Retailer",
  },
  {
    slug: "health-provider-azure", industry: "Healthcare", platform: "Microsoft Azure",
    title: "Healthcare provider modernizes securely on Microsoft Azure",
    challenge: "Aging data center and compliance risk limited the ability to deliver new digital services.",
    architecture: "Azure landing zones, Entra ID zero-trust, Defender for Cloud and Azure SQL.",
    migrationScope: "Full data-center exit with 80+ applications migrated.",
    technology: ["Azure", "Entra ID", "Defender", "Azure SQL"],
    services: ["Migration", "Security & Compliance", "Managed Cloud"],
    timeline: "9 months",
    outcome: "HIPAA-aligned platform with stronger security posture and faster delivery.",
    metrics: [{ label: "Data center exit", value: "100%" }, { label: "Security findings", value: "-64%" }, { label: "Release cadence", value: "3x" }],
    quote: "We modernized without compromising compliance—exactly what we needed.",
    quoteRole: "CIO, Healthcare Provider",
  },
  {
    slug: "fintech-multicloud", industry: "Financial Services", platform: "Multi-cloud",
    title: "Fintech builds resilient multi-cloud foundation",
    challenge: "Regulatory resilience requirements demanded workload portability across providers.",
    architecture: "Multi-cloud landing zones, cross-region DR, IaC automation and FinOps guardrails.",
    migrationScope: "Core platform deployed across Google Cloud and Azure with automated DR.",
    technology: ["Terraform", "GKE", "Azure", "FinOps"],
    services: ["Cloud Strategy", "FinOps", "Disaster Recovery"],
    timeline: "7 months",
    outcome: "Tested cross-cloud DR and transparent, optimized spend.",
    metrics: [{ label: "RTO", value: "< 30 min" }, { label: "Cloud spend", value: "-31%" }, { label: "DR tests", value: "Quarterly" }],
    quote: "Portability and cost control are no longer at odds for us.",
    quoteRole: "Head of Platform, Fintech",
  },
];

export const LEADERSHIP = [
  {
    name: "Ernesto Couso",
    role: "CEO / Founder",
    photo: "/brand/ernesto.jpg", // Official approved portrait of Ernesto Couso.
    linkedin: "https://linkedin.com/in/ernestocouso",
    bio: [
      "Ernesto Couso is the Founder and Chief Executive Officer of Cloud Beaker Corporation, where he leads the company’s mission to help organizations unlock the full potential of the cloud. A technologist at heart, Ernesto has built his career at the intersection of cloud architecture, enterprise infrastructure and digital transformation—partnering with businesses to turn ambitious ideas into secure, scalable and measurable outcomes.",
      "He founded Cloud Beaker on a simple conviction: that world-class cloud expertise should be accessible, unbiased and genuinely partner-first. Under his leadership, the company delivers strategy, engineering, security and managed services across Google Cloud, Microsoft Azure and Alibaba Cloud—always anchored to the results that matter most to each client.",
      "Ernesto is passionate about building high-performing teams, championing engineering excellence, and helping customers adopt emerging technologies like AI responsibly and with purpose. He believes technology is most powerful when it creates real opportunity for the people and organizations it serves.",
    ],
    focus: [
      "Cloud architecture",
      "Enterprise infrastructure",
      "Digital transformation",
      "Multi-cloud strategy",
      "AI & data",
      "Cloud security",
      "Managed services",
      "Team leadership",
    ],
    quote: "The cloud isn’t just about technology — it’s about creating opportunities for people, organizations and a better future.",
  },
];

// Thought leadership & publications by Cloud Beaker's founder & CEO, Ernesto Couso.
export const PUBLICATIONS_INTRO =
  "Cloud Beaker's point of view is grounded in original research and thought leadership. Our Founder and CEO, Ernesto Couso, has published peer-reviewed research and books on cloud adoption, digital transformation and technology leadership — including the AOD Model, our foundational cloud-migration framework.";

export const AMAZON_AUTHOR_URL = "https://www.amazon.com/s?k=Ernesto+Couso&i=stripbooks";

export const PUBLICATIONS = [
  {
    id: "aod-model",
    featured: true,
    title: "AOD Model: A Case-Study-Based, Comprehensive Guide to a Novel Cloud Migration Strategy",
    venue: "Open Science Journal",
    year: "2023",
    type: "Peer-reviewed research",
    url: "https://doi.org/10.23954/osj.v8i2.3385",
    description:
      "Introduces the AOD (Adapt, Optimize, Deliver) framework — a structured, iterative approach to migrating and modernizing legacy systems in the cloud, validated through a real-world enterprise case study.",
    cta: "Read the whitepaper",
  },
  {
    id: "transform-ascend",
    title: "Transform and Ascend: A Guide to Cloud Adoption and Business Digital Transformation",
    venue: "Amazon",
    year: "2023",
    type: "Book",
    url: "https://www.amazon.com/Transform-Ascend-Adoption-Business-Transformation-ebook/dp/B0CNNRBD25",
    description:
      "A practical guide for leaders navigating cloud adoption — from strategy and cloud-model selection to implementation, culture change and post-adoption management.",
    cta: "View on Amazon",
  },
  {
    id: "tech-executives-playbook",
    title: "The Tech Executive's Playbook: Navigating Innovation and Leading Change",
    venue: "Amazon",
    year: "",
    type: "Book",
    url: "https://www.amazon.com/s?k=The+Tech+Executive%27s+Playbook+Ernesto+Couso&i=stripbooks",
    description:
      "A framework for technology leaders on running modern IT organizations — covering technical debt, talent, ethics and building high-performing, multicultural teams.",
    cta: "View on Amazon",
  },
];

export const WHY_POINTS = [
  { icon: "Cloud", title: "Multi-cloud by design", text: "Unbiased expertise across Google Cloud, Microsoft Azure and Alibaba Cloud—so you get the right platform for each workload, never a one-size-fits-all agenda." },
  { icon: "Users", title: "Senior-led architecture", text: "Work directly with experienced architects and engineers who have delivered complex transformations—no hand-offs to junior teams once the deal is signed." },
  { icon: "Layers", title: "Licensing + engineering + managed services", text: "A complete approach from strategy to ongoing operations—licensing, engineering and managed services delivered by one accountable partner." },
  { icon: "Globe", title: "Global delivery mindset", text: "Support for your business wherever you operate, with 24/7 coverage and a delivery model built for global scale and consistency." },
];

export const LEAD_TYPES = [
  { value: "architect", label: "Talk to an Architect" },
  { value: "cloud_assessment", label: "Cloud Assessment" },
  { value: "migration_assessment", label: "Migration Assessment" },
  { value: "managed_services", label: "Managed Services Consultation" },
  { value: "contact", label: "General Enquiry" },
];
